import { useCallback, useState, useEffect } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import { GET_USER_DATA } from '../../../../../config/apiPaths';
import { getUrlParams, getUrls, processApiPath } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';

interface InsightsContainerState {
    insightsData?: Record<string, any>; // result of the GET_CLASS_DATA_PATH api call
}

type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

const useInsightsContainer = (state: InsightsContainerState = {}) => {
    console.log('InsightsContainer');
    const { api } = getUrls();

    const [insightsData, setInsightsData] = useState<any>();

    const { t: tCommon } = useTranslation('common');

    const { authMode } = useContainer(AdminAuthContainer);

    const search = new URLSearchParams(window.location.search);
    const insightsUuid = search.get('insights_uuid') || window.localStorage.getItem('insights_uuid');

    const [getInsightsStatus, setGetInsightsStatus] = useState<LoadingStatus>('INITIAL');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { insightsId: insightsIdV1 } = useParams<{ insightsId?: string }>();
    const { insights_id: insightsIdV2 } = getUrlParams();
    const insightsId = insightsIdV1 || insightsIdV2 || '';

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            return tCommon('errorMessage');
        },
        [tCommon]
    );

    const getInsightsDataCall = useCallback(async () => {
        const { data } = await axios.get(
            processApiPath(GET_USER_DATA, undefined, {
                insights_uuid: insightsUuid!,
                insights_id: insightsId,
                auth_mode: authMode
            })
        );

        if (data?.success === false) {
            throw Error(data?.error);
        }
        return data;
    }, [insightsUuid, insightsId, authMode]);

    const getInsights = useCallback(async () => {
        setGetInsightsStatus('LOADING');

        try {
            const data = state.insightsData ?? (await getInsightsDataCall());

            setInsightsData(data);
            setGetInsightsStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetInsightsStatus('ERROR');
        }
    }, [state.insightsData, getInsightsDataCall, returnErrorMessage]);

    useEffect(() => {
        if (insightsId && insightsUuid) {
            getInsights();
        }
    }, [insightsId, getInsights, insightsUuid]);

    return {
        insightsId,
        api,
        insightsData,
        getInsightsStatus,
        errorMessage
    };
};

const InsightsContainer = createContainer(useInsightsContainer);

export default InsightsContainer;
